import { useCallback, useMemo } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';

import { Menu, Item, Section } from '@/components/Menu';

import { usePortfolios } from '@/hooks/portfolio/usePortfolio';
import type { Portfolios } from '@/hooks/portfolio/usePortfolio';

import {
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_NEW,
  ROUTE_MY_PORTFOLIOS,
} from '@/constants/routes';

import { Option } from './Option';
import { CreateNewPortfolioOption } from './CreateNewPortfolioOption';
import { AggregatePortfolioOption } from './AggregatePortfolioOption';

type Key = Parameters<
  NonNullable<ComponentPropsWithoutRef<typeof Menu>['onAction']>
>[0];

type Props = {
  portfolioId: string | null;
  children: ReactNode;
};

const AGGREGATED_PORTFOLIO_KEY = 'aggregated-portfolio';
const CREATE_NEW_PORTFOLIO_KEY = 'create-new-portfolio';

const Dropdown = ({
  portfolios,
  portfolioId,
  children,
}: Props & {
  portfolios: Portfolios;
}) => {
  const dispatch = useDispatch();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'dropDown',
  });

  const portfolioItems = useMemo(
    () =>
      portfolios.map((portfolio) => {
        const excludedHoldings = portfolio.holdings.filter(
          (holding) => holding.isAnalysisRestricted === true
        );
        const holdings = portfolio.holdings.filter(
          (holding) => holding.isAnalysisRestricted === false
        );
        const totalHoldingsCount = portfolio.holdings.length;
        const isPortfolioLimited = portfolio.isAnalysisRestricted;
        return {
          label: portfolio.name,
          value: portfolio.id,
          excludedHoldingsCount: excludedHoldings.length || null,
          holdingsCount: holdings.length || null,
          totalHoldingsCount,
          isPortfolioLimited,
        };
      }),
    [portfolios]
  );

  const items = useMemo(
    () => [
      {
        label: 'Portfolios',
        value: 'portfolios',
        children: [
          {
            label: 'All portfolios',
            value: AGGREGATED_PORTFOLIO_KEY,
          },
          ...portfolioItems,
        ],
      },
      {
        label: 'Other options',
        value: 'other-options',
        children: [
          {
            label: 'Create new portfolio',
            value: CREATE_NEW_PORTFOLIO_KEY,
          },
        ],
      },
    ],
    [portfolioItems]
  );

  const handleAction = useCallback(
    (value: Key) => {
      switch (value) {
        case AGGREGATED_PORTFOLIO_KEY:
          trackEvent({
            action: 'click',
            modifier: 'changePortfolio',
            portfolioId: String(value),
          });
          dispatch({ type: ROUTE_MY_PORTFOLIOS });
          break;
        case CREATE_NEW_PORTFOLIO_KEY:
          trackEvent({
            action: 'click',
            modifier: 'createNewPortfolio',
          });
          dispatch({ type: ROUTE_PORTFOLIO_NEW });
          break;
        default:
          trackEvent({
            action: 'click',
            modifier: 'changePortfolio',
            portfolioId: String(value),
          });
          dispatch({
            type: ROUTE_PORTFOLIO,
            payload: { portfolioId: String(value) },
          });
          break;
      }
    },
    [dispatch, trackEvent]
  );

  return (
    <Track>
      <Menu
        label={children}
        items={items}
        placement="bottom start"
        onAction={handleAction}
        selectionMode="single"
        selectedKeys={[portfolioId || '']}
      >
        {(item) => (
          <Section items={item.children} title={item.label} key={item.value}>
            {(item) => (
              <Item textValue={item.label} key={item.value}>
                <div className="max-w-[260px]">
                  {item.value === AGGREGATED_PORTFOLIO_KEY ? (
                    <AggregatePortfolioOption
                      {...item}
                      portfoliosCount={portfolios.length}
                      $selected={
                        portfolioId === AGGREGATED_PORTFOLIO_KEY ||
                        portfolioId === null
                      }
                    />
                  ) : item.value === CREATE_NEW_PORTFOLIO_KEY ? (
                    <CreateNewPortfolioOption {...item} />
                  ) : (
                    <Option {...item} $selected={portfolioId === item.value} />
                  )}
                </div>
              </Item>
            )}
          </Section>
        )}
      </Menu>
    </Track>
  );
};

export const PortfolioDropdown = (props: Props) => {
  const { data: portfolios = [] } = usePortfolios();
  return <Dropdown portfolios={portfolios} {...props} />;
};

export const UnauthenticatedPortfolioDropdown = (props: Props) => {
  return <Dropdown portfolios={[]} {...props} />;
};
